// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : stats hooks
//  was once located in uix/modules/stats but since uix/types depends on it has been moved here
// =============================================================================
// ---Third-party imports
import React from 'react'

// ---Framework imports
import {Sel, Actions} from 'uix/registries'
import log from 'uix/logging'
import {keys, dict, flatten, filterItems, get, items, uniq, values, groupBy} from 'ldash'
import {decodeTypes, decodeType} from 'uix/utils'
import {
  useSelector,
  useActions,
  useEffect,
  useMemo,
  useApi,
  useCallApi,
  useResettableState,
  useCallback,
} from './'

// ---Exports
export const useSummaries = (names, dname) => {
  let {dname: req_dname} = useSelector(Sel.request)
  if (!dname) {
    dname = req_dname
  }
  let avail_stats = useSelector(root => root.data.stats?.[dname])
  let reduceApi = useActions(Actions.reduceApi)

  let available = new Set(keys(avail_stats || {}))
  let neededNames = names.filter(n => !available.has(n)).map(n => n + '_summary')
  let summaryNames = names.map(n => n + '_summary')

  let statsNavlistCols = useSelector(root => root.data.statsNavlistCols)
  let navlistCols = useMemo(
    () => dict(names.map(n => [n, statsNavlistCols?.[n + '_summary']?.cols ?? []])),
    [names.join(','), statsNavlistCols]
  )

  let reducer = (dataState, data, error) => {
    if (error) {
      log.error(error)
      return {}
    }

    let summaries = data.statsGroup.$children || []
    let cleaned = row =>
      dict(
        filterItems(
          row,
          (k, v) => !k.startsWith('$') && !k.startsWith('_') && !String(v).startsWith('_')
        )
      )

    let dstats = dict(
      summaries.map(({name, splitname, duration = 0, $children, caption}) => [
        name.slice(0, -8),
        {
          _meta_: {
            caption,
            splitname,
            duration: parseFloat(duration),
          },
          ...dict(
            flatten(
              ($children || []).map(r =>
                filterItems(r, (n, v) => n == '_type').map(([n, v]) => [v, decodeTypes(cleaned(r))])
              )
            )
          ),
        },
      ])
    )
    let {stats = {}, statsNavlistCols = {}} = dataState
    if (stats[dname]) {
      dstats = {...stats[dname], ...dstats}
    }
    // merge navigatable column names
    statsNavlistCols = {...statsNavlistCols, ...data.navlist}
    return {stats: {...stats, [dname]: dstats}, statsNavlistCols}
  }
  // avail_stats changes are not really reliable here
  let [loading, setLoading] = useResettableState(false, [avail_stats])

  let getAggregationData = (names, refresh = false) => {
    if (names) {
      setLoading(true)
      reduceApi('filter/getAggregationData', {names, refresh}, reducer, {
        dname,
      })
    }
  }
  useEffect(() => getAggregationData(neededNames.join(',')), [neededNames.join(',')])

  let reload = useCallback(() => getAggregationData(summaryNames.join(','), true), [
    summaryNames.join(','),
  ])

  return useMemo(
    () => ({
      summaries: dict(names.filter(n => avail_stats?.[n]).map(n => [n, avail_stats[n]])),
      navlistCols,
      loading,
      reload,
    }),
    [avail_stats, navlistCols, loading]
  )
}

// annotate value types
const SUMMARIES = {
  datatype: {
    pdfsize: 'filesize',
    sumwordcachesize: 'filesize',
  },
  page: {
    sumcachsize: 'filesize',
    sumfulltextsize: 'filesize',
    sumwordcachesize: 'filesize',
    bytesperimage: 'filesize',
    sumfilesize: 'filesize',
    sumfilesizeexists: 'filesize',
  },
  domain: {
    sumimagesize: 'filesize',
    sumorgimagesize: 'filesize',
    sumimagecachesize: 'filesize',
    sumpdfsize: 'filesize',
    sumfulltextsize: 'filesize',
  },
  archive_store: {storedfilesize: 'filesize'},
  archive_state: {storedfilesize: 'filesize'},
}

export const useSummary = (name, dname) => {
  let {summaries, navlistCols, loading, reload} = useSummaries([name], dname)
  return useMemo(() => {
    let summary = summaries[name]
    let [sumdata, columns, info] = [{}, {types: {}, names: [], filled: []}, {}]
    if (summary) {
      let {sum, _meta_, ...rest} = summary
      sumdata = rest
      info = {..._meta_, sum}

      let filled = []
      let colnames = []

      if (sumdata) {
        colnames = keys(values(sumdata)[0])
        filled = colnames.filter(col => values(summary).some(data => data[col] > 0))
      }

      columns = {
        types: SUMMARIES[name] || {},
        names: colnames,
        filled,
      }
    }

    return {sumdata, columns, navlistCols: navlistCols[name], info, loading, reload}
  }, [name, summaries, navlistCols[name], loading])
}

export const useTimestats = ({
  tstype,
  datefield,
  interval = 'month',
  subdomains = false,
  days,
  startDate,
}) => {
  let params = {
    type: tstype,
    datefield,
    interval,
    subdomains,
  }
  if (days) {
    params = {...params, days}
  }
  if (startDate) {
    params = {...params, from: startDate}
  }

  let {callApi: updateTimeStats, calling} = useCallApi('stats/updateTimeStats')

  let {data: apidata, error, loading, reload: reloadSlice} = useApi(
    'stats/getTimeStatsSlice',
    params,
    {
      cached: true,
      autoreset: false,
    }
  )

  let reload = useCallback(() => {
    updateTimeStats({type: tstype}, () => {
      reloadSlice()
    })
  })

  return useMemo(() => {
    let res = apidata || {}
    let {tsdata, ...result} = apidata || {}
    if (!subdomains) {
      // pick first (and only)
      tsdata = values(tsdata)[0] || []
    } else {
      // summarize over all domains
      let domain_flatted_vals = values(groupBy(flatten(values(tsdata)), e => e.datetime))
      tsdata = domain_flatted_vals.map(date_stats => {
        let res = {date: date_stats[0].date, datetime: date_stats[0].datetime}
        date_stats.forEach(date_stat => {
          items(date_stat).forEach(([k, v]) => {
            if (k != 'date' && k != 'datetime') {
              if (!res[k]) {
                res[k] = 0
              }
              res[k] += parseInt(v)
            }
          })
        })
        return res
      })
    }
    return {...result, tsdata, loading: loading || calling, reload}
  }, [apidata, loading, calling])
}
